import { ReactNode, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronRight,
  faSatelliteDish,
  faTowerBroadcast,
} from '@fortawesome/free-solid-svg-icons'
import { PanelCard } from '../shared/components/ui'
import { RangeToggle, ReconfiguredMark } from './admin/missingReadings/controls'
import { useAuth } from '../hooks/useAuth'
import { useDeviceSignal, useDeviceSignalDaily } from '../hooks/useSignalStrength'
import { useDeviceMissingReadingsSeries } from '../hooks/admin/useMissingReadings'
import type { DeviceSeriesRow } from '../types/missingReadings'
import { deliveryBars, expectedSoFar, utcDayKey } from '../utils/dailyDelivery'
import DeliveryBars, { BarDateLabel, DeliveryLegend, shortDay } from './charts/DeliveryBars'
import SignalBars, { SignalLegend, stationsText } from './charts/SignalBars'
import { shareStep, stationGrid, type StationCell } from '../utils/stationGrid'
import { firstDataDay, signalBars, type SignalBar } from '../utils/signalBars'
import {
  SENSITIVITY_DBM,
  SIGNAL_MAX_DAYS,
  SIGNAL_QUALITY_LABELS,
  SIGNAL_QUALITY_TONES,
  messageTypeLabel,
  type SignalDay,
  type SignalSample,
  type SignalSummary,
} from '../types/signalStrength'

/**
 * One device's link health: Sigfox signal and, for admins, missed messages.
 *
 * Two cards side by side under one range picker. They are kept apart on
 * purpose: signal strength is an early warning about the install, missed
 * messages are what actually went missing, and a "Good" signal badge sitting
 * in the same block as a run of red days read as either a contradiction or a
 * cause. One picker still drives both, so they always cover the same window.
 *
 * Signal is for everyone - a field installer needs it, and the endpoint is
 * ownership-scoped server-side. Missed messages are admin-only: that query
 * never fires for anyone else, and the signal card then takes the full width.
 *
 * The picker offers 1-90 days to everyone and 180/365 to admins. Signal stops
 * at 90 (the API clamps there and samples are not kept longer), so past that
 * the signal figures say which window they actually cover.
 *
 * "Live" polls signal every 20s. It exists for installation: someone adjusting
 * an antenna on site needs the next transmission to appear on its own, so
 * turning it on also opens the message list. Off by default so ordinary device
 * viewing does not poll.
 */

const USER_RANGE_OPTIONS = [1, 7, 30, 90] as const
const ADMIN_RANGE_OPTIONS = [1, 7, 30, 90, 180, 365] as const
const LIVE_REFETCH_MS = 20_000
const DEFAULT_DAYS = SIGNAL_MAX_DAYS

type StationsByDay = Map<string, SignalDay['stations']>

interface Props {
  sigfoxId: string
  deviceId: number
}

export default function DeviceConnectivitySection({ sigfoxId, deviceId }: Props) {
  const { isAdmin } = useAuth()
  const admin = isAdmin()
  // 90 days by default: the longest window signal is kept for, and long enough
  // that a run of bad days stands out against the device's normal pattern.
  const [days, setDays] = useState<number>(DEFAULT_DAYS)
  const [live, setLive] = useState(false)
  const [messagesOpen, setMessagesOpen] = useState(false)
  const [missingOpen, setMissingOpen] = useState(false)

  const signalDays = Math.min(days, SIGNAL_MAX_DAYS)
  const signal = useDeviceSignal(sigfoxId, signalDays, 50, live ? LIVE_REFETCH_MS : undefined)
  const signalDaily = useDeviceSignalDaily(sigfoxId, signalDays)
  const delivery = useDeviceMissingReadingsSeries(deviceId, days, admin)
  const stationsByDay = useMemo(
    () => new Map((signalDaily.data ?? []).map((d) => [d.day, d.stations] as const)),
    [signalDaily.data]
  )

  const toggleLive = (on: boolean) => {
    setLive(on)
    if (on) setMessagesOpen(true)
  }

  return (
    <section aria-labelledby="connectivity-heading" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="connectivity-heading" className="text-lg font-semibold text-gray-700">
          Connectivity
        </h2>
        <RangeToggle value={days} onChange={setDays} options={admin ? ADMIN_RANGE_OPTIONS : USER_RANGE_OPTIONS} />
      </div>

      {/* No items-start: the two cards sit side by side and are read as a pair,
          so they stretch to a shared height. Left to their natural sizes the
          shorter one stops mid-air beside the taller, which reads as a rendering
          fault rather than as "this card simply has less to say". */}
      <div className={`grid gap-4 ${admin ? 'lg:grid-cols-2' : ''}`}>
        {admin && (
          <PanelCard className="h-full" title="Missed messages" icon={faSatelliteDish}>
            <DeliveryStrip deviceId={deviceId} days={days} query={delivery} stationsByDay={stationsByDay} />
            {delivery.data && (
              <div className="mt-4">
                <MissingDays
                  deviceId={deviceId}
                  stationsByDay={stationsByDay}
                  series={delivery.data.series}
                  now={new Date(delivery.dataUpdatedAt || Date.now())}
                  open={missingOpen}
                  onToggle={() => setMissingOpen((v) => !v)}
                />
              </div>
            )}
          </PanelCard>
        )}

        <PanelCard
          className="h-full"
          title="Signal strength"
          icon={faTowerBroadcast}
          actions={
            <label className="flex items-center gap-1 text-xs text-gray-600">
              <input type="checkbox" checked={live} onChange={(e) => toggleLive(e.target.checked)} />
              Live
            </label>
          }
        >
          {signal.isLoading && <p className="text-sm text-gray-500">Loading signal…</p>}
          {signal.isError && <p className="text-sm text-red-600">Could not load signal data.</p>}
          {signal.data && signal.data.summary.sampleCount === 0 && (
            <p className="text-sm text-gray-500">
              No signal data in the last {signalDays} day{signalDays === 1 ? '' : 's'}. Signal is recorded
              from the Sigfox callback; devices that have not reported recently will show nothing here.
            </p>
          )}
          {signal.data && signal.data.summary.sampleCount > 0 && (
            <SignalStats summary={signal.data.summary} clampedTo={days > SIGNAL_MAX_DAYS ? signalDays : null} />
          )}
          <SignalStrip days={signalDays} query={signalDaily} />
          {signal.data && signal.data.samples.length > 0 && (
            <div className="mt-4">
              <Disclosure
                label={`Recent messages (${signal.data.samples.length})`}
                open={messagesOpen}
                onToggle={() => setMessagesOpen((v) => !v)}
              >
                <RecentMessages samples={signal.data.samples} />
              </Disclosure>
            </div>
          )}
        </PanelCard>
      </div>
    </section>
  )
}

function SignalStats({ summary, clampedTo }: { summary: SignalSummary; clampedTo: number | null }) {
  const tone = SIGNAL_QUALITY_TONES[summary.quality]

  return (
    <>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4 lg:grid-cols-2 2xl:grid-cols-4">
        <Stat label="Signal">
          <span className={`inline-block px-2 py-0.5 rounded text-sm font-medium ${tone}`}>
            {SIGNAL_QUALITY_LABELS[summary.quality]}
          </span>
        </Stat>
        <Stat
          label="Median RSSI"
          sub={
            summary.rssiMin !== null && summary.rssiMax !== null
              ? `${summary.rssiMin} to ${summary.rssiMax}`
              : undefined
          }
        >
          {summary.rssiMedian === null ? '—' : `${summary.rssiMedian} dBm`}
        </Stat>
        <Stat label="Margin" sub={`above ${SENSITIVITY_DBM} dBm floor`}>
          {summary.marginDb === null ? '—' : `${summary.marginDb.toFixed(0)} dB`}
        </Stat>
        <Stat
          label="Base stations"
          sub={summary.stations.length > 0 ? summary.stations.join(', ') : undefined}
        >
          {summary.stations.length || '—'}
        </Stat>
      </dl>

      {clampedTo !== null && (
        <p className="mt-2 text-xs text-gray-400">
          Signal figures cover the last {clampedTo} days; samples are not kept longer.
        </p>
      )}

      {/* A lone base station is a genuine fragility even at a comfortable
          margin: nothing takes over when that path fades. In the field data this
          was the difference between ~7% and ~91% delivery at similar RSSI. */}
      {summary.stations.length === 1 && (
        <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          Only one base station hears this device, so there is no fallback if that path fades.
          Raising or reorienting the antenna is the usual remedy.
        </p>
      )}
    </>
  )
}

function Stat({ label, sub, children }: { label: string; sub?: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="text-base font-semibold text-gray-900">
        {children}
        {sub && <span className="block text-xs font-normal text-gray-500">{sub}</span>}
      </dd>
    </div>
  )
}

/**
 * One bar per UTC day, oldest on the left, laid out like the delivery strip so
 * the two cards read as siblings. Height is that day's median margin above the
 * Sigfox floor; colour is that day's rating. Days before capture began, or with
 * nothing heard, are pale stubs rather than gaps, so the window keeps its width.
 */
function SignalStrip({ days, query }: { days: number; query: ReturnType<typeof useDeviceSignalDaily> }) {
  const { data, isLoading, isError, dataUpdatedAt } = query
  const bars = useMemo(
    () => (data ? signalBars(data, days, new Date(dataUpdatedAt || Date.now())) : []),
    [data, days, dataUpdatedAt]
  )
  const firstDay = firstDataDay(bars)

  return (
    <div className="mt-4">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="text-xs uppercase tracking-wide text-gray-500">Daily signal</span>
        <span className="text-[11px] text-gray-500">median per day · colour shows the rating</span>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading...</p>
      ) : isError ? (
        <p className="text-sm text-gray-400">Could not load signal history</p>
      ) : data === null ? (
        <p className="text-sm text-gray-400">Daily signal will appear once the API is updated.</p>
      ) : !firstDay ? (
        <p className="text-sm text-gray-400">No signal recorded in the last {days} days</p>
      ) : (
        <div style={{ maxWidth: bars.length * 26 }}>
          <SignalBars bars={bars} />
          {firstDay > bars[0].day && (
            <p className="mt-1 text-[11px] text-gray-500">Signal tracking started {firstDay}; earlier days have no data.</p>
          )}
        </div>
      )}

      {/* A text legend, so the bars never rely on colour alone. */}
      {firstDay && <SignalLegend className="mt-2" />}
      {firstDay && <StationGrid bars={bars} />}

      <p className="mt-2 text-[11px] text-gray-500">
        Signal is an early warning about the install. It is a separate check and does not show which messages were missed.
      </p>
    </div>
  )
}

/**
 * Density, one hue: a darker cell is a larger share of that day's messages.
 * Indigo, not the sky ramp the signal strip uses, so "darker" is never read as
 * "better signal" across the two charts.
 */
const SHARE_TONES = ['bg-gray-100', 'bg-indigo-100', 'bg-indigo-300', 'bg-indigo-500', 'bg-indigo-700'] as const

const SHARE_LEGEND: { step: 1 | 2 | 3 | 4; label: string }[] = [
  { step: 1, label: '<25%' },
  { step: 2, label: '25-50%' },
  { step: 3, label: '50-75%' },
  { step: 4, label: '75%+' },
]

/**
 * Which base stations carried each day's messages: a row per station, a cell
 * per day, aligned with the Daily signal strip above. Shading is the station's
 * share of that day. A device that switches station, or loses its second one,
 * shows as a row going pale - which a single "stations: 2" figure hides.
 */
function StationGrid({ bars }: { bars: SignalBar[] }) {
  const rows = useMemo(() => stationGrid(bars), [bars])
  if (rows.length === 0) return null

  return (
    <div className="mt-4">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="text-xs uppercase tracking-wide text-gray-500">Base stations</span>
        <span className="text-[11px] text-gray-500">share of each day's messages</span>
      </div>
      <div style={{ maxWidth: bars.length * 26 }} className="space-y-1.5">
        {rows.map((row) => (
          <div key={row.station}>
            <div className="mb-0.5 flex justify-between text-[11px] text-gray-600">
              <span className="font-mono">{row.station}</span>
              <span className="tabular-nums text-gray-500">{row.total.toLocaleString()} msgs</span>
            </div>
            <div className="flex h-3 items-end gap-px">
              {row.cells.map((cell) => {
                const step = shareStep(cell.share)
                const title = stationCellTitle(row.station, cell)
                return (
                  <span key={cell.day} title={title} aria-label={title} className="group relative flex h-full min-w-0 flex-1 items-end">
                    <BarDateLabel day={cell.day} />
                    <span className={`w-full rounded-sm ${SHARE_TONES[step]}`} style={{ height: step === 0 ? '30%' : '100%' }} />
                  </span>
                )
              })}
            </div>
          </div>
        ))}
      </div>
      <ul className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-600">
        {SHARE_LEGEND.map(({ step, label }) => (
          <li key={step} className="flex items-center gap-1">
            <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-sm ${SHARE_TONES[step]}`} />
            {label}
          </li>
        ))}
        <li className="flex items-center gap-1">
          <span aria-hidden="true" className={`h-1 w-2.5 rounded-sm ${SHARE_TONES[0]}`} />
          not heard
        </li>
      </ul>
    </div>
  )
}

function stationCellTitle(station: string, cell: StationCell): string {
  if (cell.dayTotal === 0) return `${shortDay(cell.day)} · ${station}: no station data`
  if (cell.count === 0) return `${shortDay(cell.day)} · ${station}: not heard (0 of ${cell.dayTotal})`
  const pct = Math.round(cell.share * 100)
  const rssi = cell.rssiMedian === null ? '' : `, median ${cell.rssiMedian} dBm`
  return `${shortDay(cell.day)} · ${station}: ${cell.count} of ${cell.dayTotal} messages (${pct}%)${rssi}`
}

/**
 * One bar per day, oldest on the left. Colour is missed messages (exact);
 * height is received against expected (the estimate). Each bar links to that
 * day's sequence detail, as the table rows did.
 */
function DeliveryStrip({
  deviceId,
  days,
  query,
  stationsByDay,
}: {
  deviceId: number
  days: number
  query: ReturnType<typeof useDeviceMissingReadingsSeries>
  stationsByDay: StationsByDay
}) {
  const { data, isLoading, isError, dataUpdatedAt } = query
  const bars = useMemo(
    () => (data ? deliveryBars(data.series, new Date(dataUpdatedAt || Date.now())) : []),
    [data, dataUpdatedAt]
  )
  const totalMissed = useMemo(() => bars.reduce((sum, b) => sum + b.row.missedMessages, 0), [bars])
  const daysAffected = useMemo(() => bars.filter((b) => b.row.missedMessages > 0).length, [bars])
  const windowLabel = days === 1 ? 'last day' : `last ${days} days`

  return (
    <div>
      {bars.length > 0 && (
        <dl className="mb-4 grid grid-cols-2 gap-x-6 gap-y-3">
          <Stat label="Missed" sub={`messages, ${windowLabel}`}>
            <span className={totalMissed > 0 ? 'text-red-600' : ''}>{totalMissed.toLocaleString()}</span>
          </Stat>
          <Stat label="Days affected" sub="with at least one missed">
            {daysAffected} of {bars.length}
          </Stat>
        </dl>
      )}

      <span className="mb-2 block text-xs uppercase tracking-wide text-gray-500">Daily delivery</span>
      {isLoading ? (
        <p className="text-sm text-gray-400">Loading...</p>
      ) : isError ? (
        <p className="text-sm text-gray-400">Could not load missing-readings history</p>
      ) : bars.length === 0 ? (
        <p className="text-sm text-gray-400">No message activity in the last {days} days</p>
      ) : (
        <>
          <DeliveryBars
            bars={bars}
            hrefForDay={(day) => `/missing-readings/${day}/${deviceId}`}
            titleSuffix={(day) => {
              const text = stationsText(stationsByDay.get(day))
              return text ? ` · stations: ${text}` : ''
            }}
          />
          <div className="mt-2">
            <DeliveryLegend />
          </div>
        </>
      )}
    </div>
  )
}

/** Only the days that lost messages, newest first; the strip already shows the rest. */
function MissingDays({
  deviceId,
  series,
  now,
  open,
  onToggle,
  stationsByDay,
}: {
  deviceId: number
  stationsByDay: StationsByDay
  series: DeviceSeriesRow[]
  now: Date
  open: boolean
  onToggle: () => void
}) {
  const rows = useMemo(() => series.filter((row) => row.missedMessages > 0).reverse(), [series])
  const today = utcDayKey(now)

  if (rows.length === 0) return null

  return (
    <Disclosure label={`Days with missed messages (${rows.length})`} open={open} onToggle={onToggle}>
      <div className="max-h-72 overflow-y-auto pr-3 [scrollbar-gutter:stable]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white">
            <tr className="text-gray-400 border-b border-gray-100">
              <th className="py-1 pr-2 text-left font-medium">Day</th>
              <th className="py-1 pr-2 text-right font-medium">Missed</th>
              <th className="py-1 pr-2 text-right font-medium">Received</th>
              <th className="py-1 pr-2 text-right font-medium">Expected</th>
              <th className="py-1 text-left font-medium">Stations</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => {
              const expected = expectedSoFar(row, now)
              return (
                <tr key={row.day} className="hover:bg-gray-50">
                  <td className="py-1 pr-2 text-gray-700">
                    {/* A real link, not a row onClick: this is a navigation,
                        so it should middle-click and keyboard like one. */}
                    <Link
                      to={`/missing-readings/${row.day}/${deviceId}`}
                      className="text-blue-600 hover:underline"
                      title={`See which messages went missing on ${row.day}`}
                    >
                      {row.day}
                    </Link>
                    {row.reconfigured && (
                      <ReconfiguredMark title="Reconfigured this day — expected count not estimated" />
                    )}
                  </td>
                  <td className="py-1 pr-2 text-right tabular-nums text-red-600 font-semibold">
                    {row.missedMessages}
                  </td>
                  <td className="py-1 pr-2 text-right tabular-nums text-gray-600">{row.receivedReadings}</td>
                  <td className="py-1 pr-2 text-right tabular-nums text-gray-400">
                    {expected ?? '—'}
                    {row.day === today && expected !== null && ' so far'}
                  </td>
                  <td className="py-1 font-mono text-gray-500">{stationsText(stationsByDay.get(row.day)) || '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Disclosure>
  )
}

/** SNR is left out: Sigfox does not send it, so the column was always "—". */
function RecentMessages({ samples }: { samples: SignalSample[] }) {
  return (
    <div className="max-h-72 overflow-auto">
      <table className="min-w-full text-xs">
        <thead className="sticky top-0 bg-white">
          <tr className="text-left text-gray-400 border-b border-gray-100">
            <th className="py-1 pr-4 font-medium">Time (UTC)</th>
            <th className="py-1 pr-4 font-medium">Seq</th>
            <th className="py-1 pr-4 font-medium">Type</th>
            <th className="py-1 pr-4 font-medium">Station</th>
            <th className="py-1 font-medium">RSSI</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {samples.map((s) => (
            <tr key={`${s.seqNum}-${s.at}`}>
              <td className="py-1 pr-4 whitespace-nowrap tabular-nums">
                {new Date(s.at).toISOString().replace('T', ' ').slice(0, 19)}
              </td>
              <td className="py-1 pr-4 tabular-nums">{s.seqNum}</td>
              {/* Test messages are the ones an installer triggers on site,
                  so they are called out rather than blending in. */}
              <td className="py-1 pr-4">
                <span className={s.msgTypeByte === '04' ? 'font-medium text-blue-700' : ''}>
                  {messageTypeLabel(s.msgTypeByte)}
                </span>
              </td>
              <td className="py-1 pr-4">{s.station ?? '—'}</td>
              <td className="py-1 whitespace-nowrap tabular-nums">
                {s.rssi === null ? '—' : `${s.rssi} dBm`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Disclosure({
  label,
  open,
  onToggle,
  children,
}: {
  label: string
  open: boolean
  onToggle: () => void
  children: ReactNode
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide hover:text-gray-700"
        aria-expanded={open}
      >
        <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className="text-gray-400 h-3 w-3" />
        {label}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  )
}
